import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { Bell, CalendarDays, History, Home, LogOut, Megaphone, Ticket } from "lucide-react";
import { AppShell, Boton, type EntradaNav } from "@iavmx/ui";
import { useSesion } from "../sesion";
import { ENTRADAS_NAV_SEMAFORO, useCabeceraSemaforo } from "../tickets/comunes";

export function Raiz() {
  const ruta = useRouterState({ select: (s) => s.location.pathname });
  const { correo, persona, cerrar, entrar, configuracion } = useSesion();
  const vista = configuracion?.vista ?? "v360";
  const esSemaforo = vista === "semaforo";
  // Persona local del Semáforo (cookie iavmx_persona_local) y su selector; solo cuentan en esa vista con VISTA_LOCAL.
  const semaforo = useCabeceraSemaforo();
  // La vista de campañas (360) enseña Campañas primero; la general (Semáforo), el tablero y los tickets.
  const entradas: {
    etiqueta: string;
    to:
      | "/"
      | "/campanas"
      | "/campanas/calendario"
      | "/campanas/historial"
      | "/campanas/avisos"
      | "/tickets"
      | "/area"
      | "/avisos";
    icono: React.ReactNode;
  }[] = esSemaforo
    ? ENTRADAS_NAV_SEMAFORO
    : [
        { etiqueta: "Inicio", to: "/", icono: <Home /> },
        { etiqueta: "Campañas", to: "/campanas", icono: <Megaphone /> },
        { etiqueta: "Calendario", to: "/campanas/calendario", icono: <CalendarDays /> },
        { etiqueta: "Historial", to: "/campanas/historial", icono: <History /> },
        { etiqueta: "Avisos", to: "/campanas/avisos", icono: <Bell /> },
        { etiqueta: "Tickets", to: "/tickets", icono: <Ticket /> },
      ];
  // Activa la entrada más específica que sea prefijo de la ruta (/campanas/123 → Campañas; /tickets/nuevo → Tickets).
  const activa = entradas
    .filter((e) => ruta === e.to || (e.to !== "/" && ruta.startsWith(`${e.to}/`)))
    .sort((a, b) => b.to.length - a.to.length)[0]?.to;
  const nav: EntradaNav[] = entradas.map((e) => ({
    etiqueta: e.etiqueta,
    href: e.to,
    icono: e.icono,
    activo: activa === e.to,
    render: ({ className, children }) => (
      <Link to={e.to} className={className}>
        {children}
      </Link>
    ),
  }));
  // En el Semáforo local la API de tickets actúa como la persona de la cookie, no como la de /api/yo.
  const personaLocalSemaforo = esSemaforo && configuracion?.modoLocal ? semaforo.persona : null;
  return (
    <AppShell
      producto={esSemaforo ? "Semáforo" : "360"}
      nav={nav}
      persona={
        personaLocalSemaforo ??
        (persona
          ? { nombre: persona.nombre, correo: persona.correo, avatar: persona.avatarUrl }
          : correo
            ? { nombre: correo.split("@")[0]!.replace(/[._]/g, " "), correo }
            : null)
      }
      otrasApps={[{ etiqueta: "Portal", href: configuracion?.urlPortal ?? "https://iavmx.com" }]}
      acciones={
        esSemaforo && semaforo.acciones ? (
          semaforo.acciones
        ) : configuracion?.modoLocal ? (
          <span className="font-ui text-xs text-tinta-tenue">Sesión local</span>
        ) : correo ? (
          <Boton variante="fantasma" tamano="sm" onClick={() => void cerrar()}>
            <LogOut /> Salir
          </Boton>
        ) : (
          <Boton variante="acento" tamano="sm" onClick={() => void entrar()}>
            Entrar
          </Boton>
        )
      }
    >
      <Outlet />
    </AppShell>
  );
}
